import { HelpCircle } from 'lucide-react';
import { Tooltip } from './Tooltip';
import type { SalaryCalculation } from '../types';

interface CTCBreakdownProps {
  calculation: SalaryCalculation;
  frequency: 'monthly' | 'yearly';
  showTaxDetails: boolean;
  setShowTaxDetails: (show: boolean) => void;
  taxRegime: 'old' | 'new';
}

export function CTCBreakdown({ calculation, frequency, showTaxDetails, setShowTaxDetails, taxRegime }: CTCBreakdownProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getEpfModeLabel = () => {
    switch (calculation.epf.wageMode) {
      case 'basicDa':
        return 'Actual Basic + DA';
      case 'labourCode':
        return 'Labour-code wage floor';
      case 'custom':
        return 'Custom PF wage';
      case 'capped':
      default:
        return 'Capped wage';
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 md:p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Salary Breakdown under {taxRegime === 'new' ? 'New Tax Regime (Tax Year 2026-27)' : 'Old Tax Regime'}
      </h2>
      
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-4 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-xl font-semibold text-gray-900">
              Net {frequency === 'monthly' ? 'Annual' : 'Monthly'} Equivalent
            </span>
            <span className="text-2xl font-bold text-green-600">
              {formatCurrency(
                frequency === 'monthly' 
                  ? calculation.netSalary * 12
                  : calculation.netSalary / 12
              )}
            </span>
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-xl font-semibold text-gray-900">
              Net {frequency === 'monthly' ? 'Monthly' : 'Annual'} Salary
            </span>
            <span className="text-2xl font-bold text-green-600">
              {formatCurrency(calculation.netSalary)}
            </span>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Earnings</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Basic Salary</span>
              <span className="font-medium">{formatCurrency(calculation.basic)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">HRA</span>
              <span className="font-medium">{formatCurrency(calculation.hra)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">DA</span>
              <span className="font-medium">{formatCurrency(calculation.da)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">LTA</span>
              <span className="font-medium">{formatCurrency(calculation.lta)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Special Allowance</span>
              <span className="font-medium">{formatCurrency(calculation.special)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Performance Bonus</span>
              <span className="font-medium">{formatCurrency(calculation.bonus)}</span>
            </div>
            {!!calculation.benefits?.employerNps && (
              <div className="flex justify-between">
                <span className="text-gray-600">Employer NPS (within CTC)</span>
                <span className="font-medium">{formatCurrency(calculation.benefits.employerNps)}</span>
              </div>
            )}
            {!!calculation.benefits?.mealCoupons && (
              <div className="flex justify-between">
                <span className="text-gray-600">Meal Coupons (within CTC)</span>
                <span className="font-medium">{formatCurrency(calculation.benefits.mealCoupons)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium pt-2 border-t">
              <span className="text-gray-700">Gross Salary</span>
              <span>{formatCurrency(calculation.grossSalary)}</span>
            </div>
            <div className="flex justify-between mt-4">
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Standard Deduction</span>
                <Tooltip content="A standard deduction is a fixed amount subtracted from total income to reduce taxable income, simplifying the tax filing process.">
                  <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <span className="font-medium text-red-600">
                -{formatCurrency(calculation.tax.standardDeduction)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Taxable Income (before other deductions)</span>
              <span className="text-gray-900 font-medium">
                {formatCurrency(calculation.grossSalary - calculation.tax.standardDeduction)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Deductions</h3>
          <div className="space-y-2">
            {calculation.epf.applicable ? (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">PF Wage Base ({getEpfModeLabel()})</span>
                  <span className="text-gray-500">
                    {formatCurrency(calculation.epf.wageBase)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">EPF Employee ({calculation.epf.employeeRate}%)</span>
                  <span className="font-medium text-red-600">
                    -{formatCurrency(calculation.epf.employee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">EPF Employer ({calculation.epf.employerRate}%)</span>
                  <span className="text-gray-500">
                    {formatCurrency(calculation.epf.employer)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                <span className="text-gray-600">EPF</span>
                <span className="text-gray-500">Not Opted</span>
              </div>
            )}

            {calculation.esi.applicable ? (
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">ESI (Employee)</span>
                  <span className="font-medium text-red-600">
                    -{formatCurrency(calculation.esi.employee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">ESI (Employer)</span>
                  <span className="text-gray-500">
                    {formatCurrency(calculation.esi.employer)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                <span className="text-gray-600">ESI</span>
                <span className="text-gray-500">Not Applicable</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Professional Tax</span>
              {calculation.pt > 0 ? (
                <span className="font-medium text-red-600">
                  -{formatCurrency(calculation.pt)}
                </span>
              ) : (
                <span className="text-gray-500">Not Applicable</span>
              )}
            </div>

            {!!calculation.benefits?.employeeNps && (
              <div className="flex justify-between">
                <span className="text-gray-600">Employee NPS (payroll)</span>
                <span className="font-medium text-red-600">-{formatCurrency(calculation.benefits.employeeNps)}</span>
              </div>
            )}
            {!!(calculation.benefits?.employerNps || calculation.benefits?.mealCoupons) && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Benefits paid outside bank salary</span>
                  <span className="font-medium text-red-600">-{formatCurrency(calculation.benefits.employerNps + calculation.benefits.mealCoupons)}</span>
                </div>
                <p className="text-xs text-gray-500">
                  Allocated from Special Allowance. Employer NPS tax deduction: {formatCurrency(calculation.benefits.employerNpsDeduction)};
                  meal coupon exemption: {formatCurrency(calculation.benefits.mealExemption)} per {frequency === 'monthly' ? 'month' : 'year'}.
                  These reduce taxable income; the full benefit value is excluded from cash take-home.
                </p>
              </div>
            )}

            <div className="flex flex-col space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Income Tax</span>
                  <button
                    onClick={() => setShowTaxDetails(!showTaxDetails)}
                    className="text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    {showTaxDetails ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
                <span className="font-medium text-red-600">
                  -{formatCurrency(calculation.tax.total)}
                </span>
              </div>

              {showTaxDetails && (
                <div className="ml-4 space-y-2 text-sm border-l-2 border-gray-200 pl-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Tax</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(calculation.tax.base)}
                    </span>
                  </div>
                  {calculation.tax.surcharge > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surcharge</span>
                      <span className="font-medium text-red-600">
                        -{formatCurrency(calculation.tax.surcharge)}
                      </span>
                    </div>
                  )}
                  {calculation.tax.marginalRelief > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Marginal Relief</span>
                      <span className="font-medium text-green-600">
                        +{formatCurrency(calculation.tax.marginalRelief)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Health & Education Cess</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(calculation.tax.cess)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between font-medium pt-4 border-t">
              <span className="text-gray-700">Summary</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Deductions</span>
              <span className="font-medium text-red-600">
                -{formatCurrency(calculation.totalDeductions)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Cost to Company Breakup</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Gross Salary</span>
              <span className="font-medium">{formatCurrency(calculation.grossSalary)}</span>
            </div>
            {calculation.epf.applicable && (
              <div className="flex justify-between">
                <span className="text-gray-600">Employer EPF Contribution</span>
                <span className="font-medium">{formatCurrency(calculation.epf.employer)}</span>
              </div>
            )}
            {calculation.esi.applicable && (
              <div className="flex justify-between">
                <span className="text-gray-600">Employer ESI Contribution</span>
                <span className="font-medium">{formatCurrency(calculation.esi.employer)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
